import React from "react";
import QRCode from "react-qr-code";
import { isVenuePolicyEmpty, resolveEventPolicyHtml, sanitizeVenuePolicyHtml } from "../lib/venuePolicyHtml";

const TICKET_QR_VALUE_PATTERN = /^(?:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|EJH-[A-Z0-9]{3,16})$/;

export const formatManualTicketId = (ticketId) => {
  const asNumber = Number(ticketId);
  if (Number.isFinite(asNumber) && Number.isInteger(asNumber)) {
    return `EJH-${String(asNumber).padStart(7, "0")}`;
  }
  return `EJH-${String(ticketId).replace(/-/g, "").slice(0, 8).toUpperCase()}`;
};

export const isValidTicketQrValue = (value) => {
  const normalized = String(value || "").trim();
  return Boolean(normalized) && TICKET_QR_VALUE_PATTERN.test(normalized);
};

export const formatReceiptPrice = (price) => `P${Number(price || 0).toLocaleString()}`;

export const formatReceiptDateTime = (date, time) => {
  const datePart = String(date || "-").trim() || "-";
  const timePart = String(time || "").trim();
  return timePart ? `${datePart} | ${timePart}` : datePart;
};

export const normalizeReceiptTicket = (ticket = {}) => {
  const statusRaw = String(ticket.statusLabel || ticket.status || "Active").trim();
  const statusLabel = /^unused$/i.test(statusRaw) ? "Active" : statusRaw || "Active";

  const attendeeName =
    String(ticket.attendeeName || ticket.attendee || ticket.attendee_name || "").trim() || "Guest";
  const attendeeEmail = String(ticket.attendeeEmail || ticket.email || "").trim() || "-";

  return {
    eventTitle: ticket.eventTitle || "Event",
    eventDate: ticket.eventDate || "",
    eventTime: ticket.eventTime || "",
    eventVenue: ticket.eventVenue || ticket.venue || "-",
    eventAddress: ticket.eventAddress || "",
    attendeeName,
    attendeeEmail,
    tierName: ticket.tierName || ticket.type || "General Admission",
    sectionDisplay: ticket.sectionDisplay || ticket.exactSection || "",
    sectionColor: ticket.sectionColor || "",
    seatLabel: ticket.seatLabel || "",
    price: Number(ticket.price || 0),
    statusLabel,
    qrCode: ticket.qrCode || "",
    manualTicketId: ticket.manualTicketId || ticket.ticketId || "",
    policyHtml: ticket.policyHtml || resolveEventPolicyHtml(ticket) || "",
  };
};

const isUsedLikeStatus = (label) => /^(used|expired|refunded)$/i.test(String(label || ""));

const Field = ({ label, children, preserveCase = false }) => (
  <div className="min-w-0">
    <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
    <div
      className={`text-black font-bold text-[11px] sm:text-sm leading-snug break-words ${
        preserveCase ? "normal-case" : "uppercase"
      }`}
    >
      {children}
    </div>
  </div>
);

const TicketReceiptCard = ({ ticket, onContactSupport }) => {
  if (!ticket) return null;

  const receipt = normalizeReceiptTicket(ticket);
  const qrValue = isValidTicketQrValue(receipt.qrCode) ? receipt.qrCode : "";
  const policyHtml = isVenuePolicyEmpty(receipt.policyHtml) ? "" : sanitizeVenuePolicyHtml(receipt.policyHtml);
  const used = isUsedLikeStatus(receipt.statusLabel);

  return (
    <div className="bg-white text-black w-full rounded-2xl p-4 sm:p-5 shadow-lg min-w-0">
      <div className="flex flex-col items-center">
        <div className="bg-black text-white text-xs font-bold tracking-wide px-3 py-1">
          <span className="text-red-600">ECHO</span>JHAM
        </div>
        <p
          className={`mt-2 text-xs font-black uppercase tracking-widest ${
            /^refunded$/i.test(receipt.statusLabel)
              ? "text-violet-600"
              : used
                ? "text-gray-400"
                : "text-emerald-600"
          }`}
        >
          {receipt.statusLabel}
        </p>
      </div>

      <div className="mt-4">
        {qrValue ? (
          <>
            <div className="flex justify-center">
              <QRCode value={qrValue} style={{ width: 148, height: 148 }} />
            </div>
            <p className="text-center text-[10px] uppercase tracking-widest text-gray-400 mt-3">Ticket ID</p>
            <p className="text-center text-sm font-bold tracking-wide break-all">{receipt.manualTicketId}</p>
          </>
        ) : /^refunded$/i.test(receipt.statusLabel) ? (
          <div className="text-center px-2 py-3">
            <p className="text-sm font-bold text-violet-700">This ticket was refunded and is no longer valid.</p>
            <p className="text-[10px] text-gray-500 mt-2 uppercase tracking-wide">QR removed · {receipt.manualTicketId}</p>
            {onContactSupport ? (
              <button
                type="button"
                onClick={onContactSupport}
                className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded bg-red-600 text-white text-[10px] font-black uppercase tracking-wide hover:bg-red-700"
              >
                Contact Support
              </button>
            ) : null}
          </div>
        ) : (
          <div className="text-center px-2 py-3">
            <p className="text-sm font-bold text-red-600">Ticket invalid, please contact support.</p>
            {onContactSupport ? (
              <button
                type="button"
                onClick={onContactSupport}
                className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded bg-red-600 text-white text-[10px] font-black uppercase tracking-wide hover:bg-red-700"
              >
                Contact Support
              </button>
            ) : null}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 mt-5 pt-4">
        <h3 className="text-lg sm:text-xl font-black uppercase text-center leading-tight break-words">
          {receipt.eventTitle}
        </h3>

        <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
          <Field label="Name" preserveCase>
            {receipt.attendeeName}
          </Field>
          <Field label="Ticket Type">{receipt.tierName}</Field>
          <Field label="Email" preserveCase>
            {receipt.attendeeEmail}
          </Field>
          <Field label="Section">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              {receipt.sectionColor ? (
                <span
                  className="h-2.5 w-2.5 rounded-sm border border-black/10 shrink-0"
                  style={{ backgroundColor: receipt.sectionColor }}
                  aria-hidden="true"
                />
              ) : null}
              <span className="min-w-0 break-words">{receipt.sectionDisplay || "-"}</span>
            </span>
          </Field>
          <Field label="Date">{formatReceiptDateTime(receipt.eventDate, receipt.eventTime)}</Field>
          <Field label="Seat">{receipt.seatLabel || "Assigned at issuance"}</Field>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] uppercase tracking-wide text-gray-400">Venue</p>
            <p className="text-black font-bold uppercase text-[11px] sm:text-sm leading-snug break-words">
              {receipt.eventVenue}
            </p>
            {receipt.eventAddress ? (
              <p className="text-[10px] text-gray-400 mt-0.5 normal-case font-normal leading-snug break-words">
                {receipt.eventAddress}
              </p>
            ) : null}
          </div>
          <Field label="Price">{formatReceiptPrice(receipt.price)}</Field>
        </div>
      </div>

      {policyHtml ? (
        <div className="relative mt-6 border border-red-600 rounded-xl px-3 pt-4 pb-3 min-w-0">
          <p className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-2 text-[10px] font-black uppercase tracking-wide text-red-600 whitespace-nowrap">
            Venue Policy Guidelines
          </p>
          <div
            className="text-[11px] text-gray-700 leading-snug break-words [&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-4 [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic [&_s]:line-through"
            dangerouslySetInnerHTML={{ __html: policyHtml }}
          />
        </div>
      ) : null}

      <p className="mt-4 text-center text-[9px] leading-relaxed text-gray-400">
        By entering the venue, you agree to abide by the rules and regulations. Management reserves the right to refuse entry.
      </p>
    </div>
  );
};

export default TicketReceiptCard;
